import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../components/ui/AuthStateManager';
import Header from '../../components/ui/Header';
import DashboardQuickActions from '../../components/ui/DashboardQuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import PlannerCard from './components/PlannerCard';
import PreferenceTemplate from './components/PreferenceTemplate';
import AccountSettings from './components/AccountSettings';
import DashboardStats from './components/DashboardStats';
import SearchAndFilter from './components/SearchAndFilter';
import { plannerService } from '../../services/plannerService';
import DeleteConfirmationModal from './components/DeleteConfirmationModal';
import { getSessionBackground } from '../../utils/backgroundRotation';

const UserAccountDashboard = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(searchParams?.get('tab') || 'overview');
  const [planners, setPlanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPlanners, setTotalPlanners] = useState(0);
  const [totalGenerated, setTotalGenerated] = useState(0);
  const [availableYears, setAvailableYears] = useState([]);
  const [filters, setFilters] = useState({ year: null, colorScheme: 'all' });
  const [sortBy, setSortBy] = useState('newest');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, planner: null });
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Add background image state
  const [backgroundImage, setBackgroundImage] = useState('');

  // Add these missing constants
  const mockStats = {
    totalPlanners: totalPlanners,
    totalGenerated: totalGenerated,
    totalDownloads: 0
  };

  const mockPlanners = planners;
  const mockTemplates = [];

  const tabs = [
  { id: 'overview', label: 'Overview', icon: 'Home' },
  { id: 'all-planners', label: 'All Planners', icon: 'FileText' },
  //{ id: 'templates', label: 'Templates', icon: 'Bookmark' },
  { id: 'settings', label: 'Settings', icon: 'Settings' }];


  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  // Load planners from database
  useEffect(() => {
    if (activeTab === 'all-planners') {
      loadPlanners();
      loadAvailableYears();
    }
  }, [activeTab, filters, sortBy, currentPage]);

  // Initialize background on component mount
  useEffect(() => {
    setBackgroundImage(getSessionBackground());
  }, []);

  const loadPlanners = async () => {
    setLoading(true);
    setError('');
    try {
      const result = await plannerService?.getUserPlanners(filters, sortBy, currentPage, 12);
      setPlanners(result?.planners);
      setTotalPages(result?.totalPages);
      setTotalPlanners(result?.totalCount);
      setTotalGenerated(result?.totalGenerated);
    } catch (err) {
      console.error('Error loading planners:', err);
      setError(err?.message || 'Failed to load planners');
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableYears = async () => {
    try {
      const years = await plannerService?.getAvailableYears();
      setAvailableYears(years);
    } catch (err) {
      console.error('Error loading years:', err);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1); // Reset to first page when filters change
  };

  const handleSortChange = (newSort) => {
    setSortBy(newSort);
    setCurrentPage(1); // Reset to first page when sort changes
  };

  const handleDeletePlanner = (planner) => {
    setDeleteModal({ isOpen: true, planner });
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal?.planner) return;

    setIsDeleting(true);
    try {
      await plannerService?.deletePlanner(deleteModal?.planner?.id);
      setDeleteModal({ isOpen: false, planner: null });

      // Reload planners to reflect deletion
      await loadPlanners();

      // Show success message
      alert('Planner deleted successfully. It still counts toward your total planners created.');
    } catch (err) {
      console.error('Error deleting planner:', err);
      alert(`Failed to delete planner: ${err?.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDownloadPlanner = async (plannerId) => {
    // This method is no longer used directly for downloads
    // Downloads are now handled within PlannerCard component
    // Keep this for backward compatibility or remove if not needed elsewhere
    try {
      await plannerService?.recordDownload(plannerId);
    } catch (err) {
      console.error('Error recording download:', err);
    }
  };

  const handleCreateNew = () => {
    navigate('/planner-setup-wizard');
  };

  const handleViewPlanner = (plannerId) => {
    navigate(`/planner-preview?id=${plannerId}`);
  };

  const handleDuplicatePlanner = (plannerId) => {
    navigate(`/planner-setup-wizard?duplicate=${plannerId}`);
  };

  const handleUseTemplate = (templateId) => {
    navigate(`/planner-setup-wizard?template=${templateId}`);
  };

  const handleEditTemplate = (templateId) => {
    console.log('Edit template:', templateId);
  };

  const handleDeleteTemplate = (templateId) => {
    console.log('Delete template:', templateId);
  };

  const handleUpdateProfile = (profileData) => {
    console.log('Update profile:', profileData);
  };

  const handleUpdatePreferences = (preferences) => {
    console.log('Update preferences:', preferences);
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-8">
            <DashboardStats stats={mockStats} />
            <DashboardQuickActions
              recentPlanners={mockPlanners?.slice(0, 3)}
              onCreateNew={handleCreateNew}
              onViewPlanner={handleViewPlanner} />

          </div>);


      case 'all-planners':
        return (
          <div className="space-y-6">
            {/* Stats Summary */}
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Total Planners</p>
                  <p className="font-heading text-2xl font-bold text-foreground">{totalPlanners}</p>
                </div>
                <div>
                  <p className="font-caption text-xs text-muted-foreground mb-1">Total Generated</p>
                  <p className="font-heading text-2xl font-bold text-foreground">{totalGenerated}</p>
                  <p className="font-caption text-xs text-muted-foreground mt-1">
                    (Includes deleted planners)
                  </p>
                </div>
                <div className="col-span-2 md:col-span-1">
                  <p className="font-caption text-xs text-muted-foreground mb-1">Current Page</p>
                  <p className="font-heading text-2xl font-bold text-foreground">
                    {currentPage} / {totalPages}
                  </p>
                </div>
              </div>
            </div>
            <SearchAndFilter
              onFilter={handleFilterChange}
              onSort={handleSortChange}
              availableYears={availableYears} />
            {loading ?
            <div className="flex items-center justify-center py-12">
                <Icon name="Loader2" size={32} className="animate-spin text-primary" />
                <span className="ml-3 font-body text-muted-foreground">Loading planners...</span>
              </div> :
            error ?
            <div className="text-center py-12">
                <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="AlertCircle" size={24} color="var(--color-destructive)" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  Error Loading Planners
                </h3>
                <p className="font-body text-muted-foreground mb-4">{error}</p>
                <Button variant="default" onClick={loadPlanners} iconName="RefreshCw" iconPosition="left">
                  Try Again
                </Button>
              </div> :
            planners?.length > 0 ?
            <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {planners?.map((planner) =>
                <PlannerCard
                  key={planner?.id}
                  planner={planner}
                  onDelete={handleDeletePlanner}
                  onDownload={handleDownloadPlanner}
                  onRefresh={loadPlanners} />
                )}
                </div>

                {/* Pagination */}
                {totalPages > 1 &&
              <div className="flex items-center justify-center space-x-2 pt-4">
                    <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  iconName="ChevronLeft"
                  iconSize={16} />

                    <span className="font-body text-sm text-muted-foreground px-4">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  iconName="ChevronRight"
                  iconSize={16} />

                  </div>
              }
              </> :

            <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="FileText" size={24} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  No planners found
                </h3>
                <p className="font-body text-muted-foreground mb-4">
                  {filters?.year || filters?.colorScheme !== 'all' ? 'No planners match your current filters. Try adjusting your selection.' : 'You haven\'t created any planners yet. Start by creating your first planner!'
                }
                </p>
                <Button
                variant="default"
                onClick={handleCreateNew}
                iconName="Plus"
                iconPosition="left">

                  Create Your First Planner
                </Button>
              </div>
            }
            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={deleteModal?.isOpen}
              onClose={() => setDeleteModal({ isOpen: false, planner: null })}
              onConfirm={handleConfirmDelete}
              plannerName={deleteModal?.planner?.name || ''}
              isDeleting={isDeleting} />
          </div>);


      case 'templates':
        return (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-heading text-xl font-semibold text-foreground">
                  Saved Templates
                </h2>
                <p className="font-body text-sm text-muted-foreground mt-1">
                  Manage your preference templates for quick planner creation
                </p>
              </div>
              <Button
                variant="default"
                onClick={() => navigate('/planner-setup-wizard?create-template=true')}
                iconName="Plus"
                iconPosition="left">

                Create Template
              </Button>
            </div>
            {mockTemplates?.length > 0 ?
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {mockTemplates?.map((template) =>
              <PreferenceTemplate
                key={template?.id}
                template={template}
                onEdit={handleEditTemplate}
                onDelete={handleDeleteTemplate}
                onUse={handleUseTemplate} />

              )}
              </div> :

            <div className="text-center py-12">
                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Bookmark" size={24} color="var(--color-muted-foreground)" />
                </div>
                <h3 className="font-heading font-semibold text-foreground mb-2">
                  No templates saved
                </h3>
                <p className="font-body text-muted-foreground mb-4">
                  Save your favorite planner settings as templates for quick access
                </p>
                <Button
                variant="outline"
                onClick={() => navigate('/planner-setup-wizard?create-template=true')}
                iconName="Plus"
                iconPosition="left">

                  Create Your First Template
                </Button>
              </div>
            }
          </div>);


      case 'settings':
        return (
          <AccountSettings
            user={user}
            onUpdateProfile={handleUpdateProfile}
            onUpdatePreferences={handleUpdatePreferences} />);



      default:
        return null;
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      {/* Background Image with Overlay - Random rotation per session */}
      <div 
        className="fixed inset-0 bg-cover bg-center bg-no-repeat"
        style={{
          backgroundImage: `url("${backgroundImage}")`
        }}
      >
        {/* Lighter overlay for better image visibility */}
        <div className="absolute inset-0 bg-black/30" />
      </div>

      {/* Content wrapper with relative positioning */}
      <div className="relative min-h-screen">
        <Header isAuthenticated={true} onLogout={logout} />
        <div className="pt-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="font-heading text-3xl font-bold text-white drop-shadow-lg">
                    Dashboard
                  </h1>
                  <p className="font-body text-white/90 mt-2 text-base drop-shadow">
                    Welcome back, {user?.firstName || 'User'}! Manage your planners and account settings.
                  </p>
                </div>
                
                <Button
                  variant="default"
                  onClick={handleCreateNew}
                  iconName="Plus"
                  iconPosition="left"
                  className="hidden sm:flex">
                  Create New Planner
                </Button>
              </div>
            </div>

            {/* Navigation Tabs - REMOVED glassmorphism */}
            <div className="mb-8">
              <div className="bg-white/95 border border-gray-200 rounded-lg overflow-hidden">
                <nav className="flex space-x-8 overflow-x-auto px-4">
                  {tabs?.map((tab) =>
                    <button
                      key={tab?.id}
                      onClick={() => handleTabChange(tab?.id)}
                      className={`
                        flex items-center space-x-2 py-4 px-1 border-b-2 font-body font-medium text-sm
                        whitespace-nowrap transition-colors duration-200
                        ${activeTab === tab?.id
                          ? 'border-primary text-primary' :'border-transparent text-gray-600 hover:text-primary hover:border-primary/50'}
                      `}
                    >
                      <Icon name={tab?.icon} size={16} />
                      <span>{tab?.label}</span>
                    </button>
                  )}
                </nav>
              </div>
            </div>

            {/* Tab Content - REMOVED glassmorphism background */}
            <div className="animate-fade-in">
              <div className="bg-white/95 border border-gray-200 rounded-lg p-6">
                {renderTabContent()}
              </div>
            </div>

            {/* Mobile Create Button */}
            <div className="fixed bottom-6 right-6 sm:hidden">
              <Button
                variant="default"
                size="lg"
                onClick={handleCreateNew}
                iconName="Plus"
                className="rounded-full shadow-lg" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserAccountDashboard;